import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { Decimal } from '~/composables/support/decimal'
import { useLayoutMode } from '~/composables/use-layout-mode'

type BeginCheckoutGaItem = {
    item_id: string
    item_name: string
    coupon: string
    discount: number
    item_variant: string
    location_id: string
    price: number
    quantity: number
    index: number
    plan_type: string | undefined
    plan_name: string | undefined
    enrollment_type: string | undefined
}

type BeginCheckoutMetaContent = {
    id: string
    quantity: number
    item_name: string
    coupon: string
    discount: number
    item_variant: string
    price: number
}

function emitBeginCheckoutGaAndDataLayer(input: {
    shouldTrackGtag: boolean
    shouldTrackDataLayer: boolean
    currencyFormat: string
    subtotal: string
    items: BeginCheckoutGaItem[]
}) {
    const eventData = {
        value: Number(input.subtotal),
        currency: input.currencyFormat,
        items: input.items
    }

    if (input.shouldTrackGtag) {
        try {
            // @ts-ignore : ignore gtag not defined
            gtag('event', 'begin_checkout', eventData)
        } catch {
            // no-op
        }
    }

    if (input.shouldTrackDataLayer) {
        try {
            // @ts-ignore : Ignore dataLayer object not defined
            window.dataLayer.push({
                event: 'begin_checkout',
                ...eventData
            })
        } catch {
            // no-op
        }
    }
}

function emitBeginCheckoutMeta(input: {
    currencyFormat: string
    subtotal: string
    contents: BeginCheckoutMetaContent[]
}) {
    try {
        // @ts-ignore
        if (!window.fbq) {
            return
        }
    } catch {
        return
    }

    try {
        // @ts-ignore
        fbq('track', 'InitiateCheckout', {
            value: Number(input.subtotal),
            currency: input.currencyFormat,
            num_items: input.contents.reduce((sum, content) => {
                return sum + content.quantity
            }, 0),
            content_ids: input.contents.map(content => content.id),
            contents: input.contents
        })
    } catch {
        // no-op
    }
}

export const AnalyticsCreateCheckoutResultFragment = graphql(`
    fragment AnalyticsCreateCheckoutResultFragment on CreateCheckoutResult {
        record {
            id
            subtotal
            inclusiveTaxTotal
            exclusiveTaxTotal
            facility {
                shortName
                organization {
                    googleAnalyticsTag
                    googleAnalyticsTagEcommerceEvents
                    googleAnalyticsTagKioskEcommerceEvents
                    dataLayerEcommerceEvents
                    dataLayerKioskEcommerceEvents
                    metaPixel
                    metaPixelEcommerceEvents
                    metaPixelKioskEcommerceEvents
                    currencyFormat
                }
            }
            items {
                __typename
                ... on CartContractItem {
                    lineItems {
                        quantity
                        unitPrice
                        originalUnitPrice
                        promotionCode
                        productVariant {
                            analyticsIdentifier
                            variantDisplay
                            product {
                                description
                            }
                        }
                        enrollment {
                            enrollmentType {
                                singular
                            }
                        }
                    }
                    contract {
                        __typename
                        ... on PlanContract {
                            planType
                            publicName
                        }
                    }
                }
            }
        }
    }
`)

export function useCreateCheckoutAnalytics() {
    const { isKiosk } = useLayoutMode()

    function emitCreateCheckoutEvent(
        createCheckoutResult: FragmentType<
            typeof AnalyticsCreateCheckoutResultFragment
        >
    ) {
        if (isKiosk.value) {
            return
        }

        const createCheckoutResultData = getFragmentData(
            AnalyticsCreateCheckoutResultFragment,
            createCheckoutResult
        )

        if (createCheckoutResultData.record === null) {
            return
        }

        const shouldTrackGtag =
            createCheckoutResultData.record.facility.organization
                .googleAnalyticsTag !== '' &&
            createCheckoutResultData.record.facility.organization
                .googleAnalyticsTagEcommerceEvents
        const shouldTrackDataLayer =
            createCheckoutResultData.record.facility.organization
                .dataLayerEcommerceEvents
        const shouldTrackMeta =
            createCheckoutResultData.record.facility.organization.metaPixel !==
                '' &&
            createCheckoutResultData.record.facility.organization
                .metaPixelEcommerceEvents

        if (!shouldTrackGtag && !shouldTrackDataLayer && !shouldTrackMeta) {
            return
        }

        const gaItems: BeginCheckoutGaItem[] = []
        const metaContents: BeginCheckoutMetaContent[] = []

        createCheckoutResultData.record.items.forEach((item, itemIndex) => {
            if (item.__typename !== 'CartContractItem') {
                return
            }

            let contractInfo:
                | { planType: string; planName: string }
                | undefined = undefined
            if (
                item.contract !== null &&
                item.contract.__typename === 'PlanContract'
            ) {
                contractInfo = {
                    planType: item.contract.planType,
                    planName: item.contract.publicName
                }
            }

            item.lineItems.forEach((lineItem, lineItemIndex) => {
                gaItems.push({
                    item_id: lineItem.productVariant.analyticsIdentifier,
                    item_name: lineItem.productVariant.product.description,
                    coupon: lineItem.promotionCode,
                    discount: Number(
                        new Decimal(lineItem.originalUnitPrice)
                            .minus(new Decimal(lineItem.unitPrice))
                            .abs()
                            .toFixed(2)
                    ),
                    item_variant: lineItem.productVariant.variantDisplay,
                    location_id:
                        createCheckoutResultData.record.facility.shortName,
                    price: Number(lineItem.unitPrice),
                    quantity: lineItem.quantity,
                    index: itemIndex + lineItemIndex,
                    plan_type: contractInfo?.planType,
                    plan_name: contractInfo?.planName,
                    enrollment_type:
                        lineItem.enrollment?.enrollmentType.singular
                })

                metaContents.push({
                    id: lineItem.productVariant.analyticsIdentifier,
                    quantity: lineItem.quantity,
                    item_name: lineItem.productVariant.product.description,
                    coupon: lineItem.promotionCode,
                    discount: Number(
                        new Decimal(lineItem.originalUnitPrice)
                            .minus(new Decimal(lineItem.unitPrice))
                            .abs()
                            .toFixed(2)
                    ),
                    item_variant: lineItem.productVariant.variantDisplay,
                    price: Number(lineItem.unitPrice)
                })
            })
        })

        if (shouldTrackGtag || shouldTrackDataLayer) {
            emitBeginCheckoutGaAndDataLayer({
                shouldTrackGtag,
                shouldTrackDataLayer,
                currencyFormat:
                    createCheckoutResultData.record.facility.organization
                        .currencyFormat,
                subtotal: createCheckoutResultData.record.subtotal,
                items: gaItems
            })
        }

        if (shouldTrackMeta) {
            emitBeginCheckoutMeta({
                currencyFormat:
                    createCheckoutResultData.record.facility.organization
                        .currencyFormat,
                subtotal: createCheckoutResultData.record.subtotal,
                contents: metaContents
            })
        }
    }

    return {
        emitCreateCheckoutEvent
    }
}
